/**
 * Baseline compare toggle, set-baseline modal, and revision refresh for /schedule/tasks.
 * Route: /schedule/tasks.
 * Does not own the DHTMLX chart engine or save/discard session sync.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { invalidateRequestTrackingCache } from '../../request-tracking';
import { notifications } from '@mantine/notifications';
import { collectSoftDeletedBaselineIndices } from '../../schedule-baselines/logic/baselineHistoryUtils';
import { fetchBaselineRevisionHistory, createBaselineRevisionDraft, createProjectBaseline, submitBaselineRevision, refreshBaselineRevision, discardBaselineRevision, approveBaselineRevision, rejectBaselineRevision } from '../../../api/baselineApis';
import { listAvailableBaselineOptions, summarizeCurrentVsBaseline } from '../../../gantt/baselineCompareUtils';
import type { SetProjectBaselineStep, SetBaselineConfirmOptions } from '../components/SetProjectBaselineModal';

export function useGanttBaselines(p: Record<string, any>) {
  const {
    store,
    dhtmlxTasks,
    projectId,
    saving,
    canSetBaseline,
    discarding,
    exporting,
    refreshActiveRevision,
    handleSaveToBackend,
    openRequestTracker,
    refreshBaselineGuard,
    setLoadingProgress,
    reloadScheduleFromServer,
    activeRevision,
    baselineInProgress,
  } = p;

const [baselineModalOpen, setBaselineModalOpen] = useState(false);
const [baselineModalStep, setBaselineModalStep] = useState<SetProjectBaselineStep>('confirm');
const [baselineSubmitting, setBaselineSubmitting] = useState(false);
const [baselineModalBlockedMessage, setBaselineModalBlockedMessage] = useState<string | null>(
  null,
);
const [revisionActionBusy, setRevisionActionBusy] = useState(false);
/** Compare mode: current schedule vs selected baseline index (B vs B deferred). */
const [compareBaselinesEnabled, setCompareBaselinesEnabled] = useState(false);
const [compareBaselineIndex, setCompareBaselineIndex] = useState<number | null>(null);
/** Soft-deleted baseline slot indexes from revision history (isDeleted). */
const [softDeletedBaselineIndices, setSoftDeletedBaselineIndices] = useState<Set<number>>(
  () => new Set(),
);
/** True only after we have observed baselineInProgress === true (job visible in tracker). */
const baselineWasInProgressRef = useRef(false);
/**
 * True after create API succeeds until the tracker reports an active job.
 * Prevents the completion effect from treating "started but not listed yet" as finished.
 */
const baselineStartPendingRef = useRef(false);
/** Avoid retry loops if auto REJECTED→Draft refresh fails. */
const rejectedSnapshotRefreshAttemptedRef = useRef<string | null>(null);

const baselineCompareOptions = useMemo(
  () => listAvailableBaselineOptions(store.getTasks(), softDeletedBaselineIndices),
  [store, dhtmlxTasks, softDeletedBaselineIndices],
);

const baselineVarianceSummary = useMemo(() => {
  if (!compareBaselinesEnabled || compareBaselineIndex == null) return null;
  return summarizeCurrentVsBaseline(store.getTasks(), compareBaselineIndex);
}, [store, dhtmlxTasks, compareBaselinesEnabled, compareBaselineIndex]);

const compareBaselineLabel = useMemo(() => {
  if (compareBaselineIndex == null) return null;
  return (
    baselineCompareOptions.find((o) => o.index === compareBaselineIndex)?.label ??
    `Baseline ${compareBaselineIndex}`
  );
}, [baselineCompareOptions, compareBaselineIndex]);

useEffect(() => {
  if (!projectId) {
    setSoftDeletedBaselineIndices(new Set());
    return;
  }
  let cancelled = false;
  void (async () => {
    try {
      const historyResult = await fetchBaselineRevisionHistory(
        { projectId },
        { silent: true },
      );
      if (cancelled) return;
      const revisions =
        historyResult.kind === 'list'
          ? historyResult.revisions
          : historyResult.kind === 'detail'
            ? [historyResult.detail.summary]
            : [];
      setSoftDeletedBaselineIndices(collectSoftDeletedBaselineIndices(revisions));
    } catch {
      if (!cancelled) setSoftDeletedBaselineIndices(new Set());
    }
  })();
  return () => {
    cancelled = true;
  };
}, [projectId]);

useEffect(() => {
  const selectable = baselineCompareOptions.filter((o) => !o.disabled);
  if (selectable.length === 0) {
    if (compareBaselinesEnabled) setCompareBaselinesEnabled(false);
    if (compareBaselineIndex != null) setCompareBaselineIndex(null);
    return;
  }
  const currentOk =
    compareBaselineIndex != null &&
    selectable.some((o) => o.index === compareBaselineIndex);
  if (!currentOk) {
    setCompareBaselineIndex(selectable[0].index);
  }
}, [baselineCompareOptions, compareBaselineIndex, compareBaselinesEnabled]);

const closeBaselineModal = useCallback(() => {
  if (baselineSubmitting || saving) return;
  setBaselineModalOpen(false);
  setBaselineModalBlockedMessage(null);
}, [baselineSubmitting, saving]);

const handleOpenSetBaseline = useCallback(async () => {
  if (!canSetBaseline) return;
  if (
    !projectId ||
    saving ||
    discarding ||
    baselineSubmitting ||
    exporting ||
    revisionActionBusy
  ) {
    return;
  }
  const dirty =
    store.getSessionMetadata().taskCount + store.getSessionMetadata().linkCount > 0;
  if (dirty) return;

  setBaselineModalBlockedMessage(null);
  setBaselineModalStep('confirm');
  setBaselineModalOpen(true);

  try {
    const openRevision = await refreshActiveRevision();
    if (openRevision) {
      const label =
        openRevision.baselineName?.trim() || `Baseline ${openRevision.baselineIndex}`;
      const statusLabel =
        openRevision.status === 'REJECTED'
          ? 'DRAFT'
          : openRevision.status === 'SUBMITTED'
            ? 'SUBMITTED'
            : openRevision.status;
      setBaselineModalBlockedMessage(
        `An open ${statusLabel} revision already exists (${label}, rev ${openRevision.revisionNumber}). Finish or discard it from the banner below before creating another baseline.`,
      );
    }
  } catch (err) {
    console.error('[Set baseline] Active revision preflight failed:', err);
  }
}, [
  canSetBaseline,
  projectId,
  saving,
  discarding,
  baselineSubmitting,
  exporting,
  revisionActionBusy,
  store,
  refreshActiveRevision,
]);

const handleBaselineSaveAndContinue = useCallback(async () => {
  const ok = await handleSaveToBackend();
  if (!ok) return;
  setBaselineModalStep('confirm');
}, [handleSaveToBackend]);

const handleConfirmCreateBaseline = useCallback(
  async (options: SetBaselineConfirmOptions) => {
    if (!projectId || baselineSubmitting) return;
    try {
      setBaselineSubmitting(true);

      if (options.mode === 'draft') {
        await createBaselineRevisionDraft(
          {
            projectId,
            baselineName: options.baselineName,
            ...(options.baselineIndex != null
              ? { baselineIndex: options.baselineIndex }
              : {}),
          },
          { silent: true },
        );
        setBaselineModalOpen(false);
        setBaselineModalBlockedMessage(null);
        notifications.show({
          title: 'Draft baseline created',
          message: `“${options.baselineName}” saved as a draft. Submit it for approval from the banner when ready.`,
          color: 'teal',
        });
        await refreshActiveRevision();
        return;
      }

      const result = await createProjectBaseline(
        {
          projectId,
          baselineName: options.baselineName,
          ...(options.baselineIndex != null ? { baselineIndex: options.baselineIndex } : {}),
        },
        { silent: true },
      );
      const trackingId = result.requestId || result.refId;
      invalidateRequestTrackingCache();
      baselineStartPendingRef.current = true;
      setBaselineModalOpen(false);
      setBaselineModalBlockedMessage(null);
      notifications.show({
        title: 'Baseline started',
        message:
          result.message ||
          (trackingId
            ? `Request ID: ${trackingId}. Monitor progress in the request tracker.`
            : 'Project baseline creation started. Monitor progress in the request tracker.'),
        color: 'teal',
      });
      if (trackingId) {
        openRequestTracker({ refreshSignal: trackingId });
      } else {
        openRequestTracker();
      }
      void refreshBaselineGuard();
      void refreshActiveRevision();
    } catch (err) {
      console.error('[Set baseline] Failed:', err);
      baselineStartPendingRef.current = false;
      // Error notification already shown by API helpers
    } finally {
      setBaselineSubmitting(false);
    }
  },
  [
    projectId,
    baselineSubmitting,
    openRequestTracker,
    refreshBaselineGuard,
    refreshActiveRevision,
  ],
);

const runRevisionAction = useCallback(
  async (action: () => Promise<unknown>, opts?: { refreshSchedule?: boolean }) => {
    if (!projectId || revisionActionBusy) return;
    try {
      setRevisionActionBusy(true);
      await action();
      await refreshActiveRevision();
      if (opts?.refreshSchedule) {
        setLoadingProgress('Refreshing schedule after baseline revision...');
        store.setLoading(true);
        try {
          await reloadScheduleFromServer({ reason: 'baseline-rewrite' });
        } finally {
          setLoadingProgress('');
          store.setLoading(false);
        }
      }
    } catch (err) {
      console.error('[Baseline revision] Action failed:', err);
    } finally {
      setRevisionActionBusy(false);
    }
  },
  [projectId, revisionActionBusy, refreshActiveRevision, store, reloadScheduleFromServer],
);

const handleSubmitActiveRevision = useCallback(async () => {
  if (!activeRevision) return;
  const meta = store.getSessionMetadata();
  if (meta.taskCount + meta.linkCount > 0) {
    notifications.show({
      title: 'Save changes first',
      message:
        'Save your schedule changes to the server before submitting this draft revision.',
      color: 'orange',
    });
    return;
  }
  await runRevisionAction(() =>
    submitBaselineRevision({ revisionId: activeRevision.id }, { silent: true }).then((r) => {
      notifications.show({
        title: 'Revision submitted',
        message: r.message || 'Baseline revision submitted for approval.',
        color: 'teal',
      });
      return r;
    }),
  );
}, [activeRevision, runRevisionAction, store]);

const handleRefreshActiveRevision = useCallback(
  async (opts?: { silent?: boolean }) => {
    if (!activeRevision) return;
    await runRevisionAction(() =>
      refreshBaselineRevision(
        { revisionId: activeRevision.id },
        { silent: true },
      ).then((r) => {
        if (!opts?.silent) {
          notifications.show({
            title: 'Snapshot refreshed',
            message: r.message || 'Revision snapshot refreshed from the live schedule.',
            color: 'teal',
          });
        }
        return r;
      }),
    );
  },
  [activeRevision, runRevisionAction],
);

/** REJECTED → Draft is done by refreshing the snapshot; do it automatically. */
useEffect(() => {
  if (!activeRevision) {
    rejectedSnapshotRefreshAttemptedRef.current = null;
    return;
  }
  if (activeRevision.status !== 'REJECTED') {
    if (rejectedSnapshotRefreshAttemptedRef.current === activeRevision.id) {
      rejectedSnapshotRefreshAttemptedRef.current = null;
    }
    return;
  }
  if (revisionActionBusy) return;
  if (rejectedSnapshotRefreshAttemptedRef.current === activeRevision.id) return;
  rejectedSnapshotRefreshAttemptedRef.current = activeRevision.id;
  void handleRefreshActiveRevision({ silent: true });
}, [activeRevision, revisionActionBusy, handleRefreshActiveRevision]);

const handleDiscardActiveRevision = useCallback(async () => {
  if (!activeRevision || !projectId) return;
  await runRevisionAction(() =>
    discardBaselineRevision(
      {
        projectId,
        baselineIndex: activeRevision.baselineIndex,
        revisionNumber: activeRevision.revisionNumber,
      },
      { silent: true },
    ).then((r) => {
      notifications.show({
        title: 'Revision discarded',
        message: r.message || 'Open baseline revision was discarded.',
        color: 'teal',
      });
      return r;
    }),
  );
}, [activeRevision, projectId, runRevisionAction]);

const handleApproveActiveRevision = useCallback(
  async (opts: { setAsEarnedValueBaseline: boolean; reviewComment?: string }) => {
    if (!activeRevision) return;
    await runRevisionAction(
      () =>
        approveBaselineRevision(
          {
            revisionId: activeRevision.id,
            setAsEarnedValueBaseline: opts.setAsEarnedValueBaseline,
            reviewComment: opts.reviewComment,
          },
          { silent: true },
        ).then((r) => {
          notifications.show({
            title: 'Revision approved',
            message: r.message || 'Baseline revision approved and promoted.',
            color: 'teal',
          });
          return r;
        }),
      { refreshSchedule: true },
    );
  },
  [activeRevision, runRevisionAction],
);

const handleRejectActiveRevision = useCallback(
  async (opts: { reviewComment: string }) => {
    if (!activeRevision) return;
    const revisionId = activeRevision.id;
    await runRevisionAction(async () => {
      const r = await rejectBaselineRevision(
        {
          revisionId,
          reviewComment: opts.reviewComment,
        },
        { silent: true },
      );
      notifications.show({
        title: 'Revision rejected',
        message: r.message || 'Baseline revision was rejected.',
        color: 'teal',
      });
      // Move rejected revision back to draft by refreshing the live snapshot.
      await refreshBaselineRevision({ revisionId }, { silent: true });
      return r;
    });
  },
  [activeRevision, runRevisionAction],
);

// When an in-flight baseline job finishes, refresh schedule so baselineInfo updates.
useEffect(() => {
  if (baselineInProgress) {
    baselineWasInProgressRef.current = true;
    baselineStartPendingRef.current = false;
    return;
  }

  // Create succeeded but tracker has not listed the job yet — do not treat as finished.
  if (baselineStartPendingRef.current) {
    return;
  }

  if (!baselineWasInProgressRef.current || !projectId) {
    return;
  }
  baselineWasInProgressRef.current = false;
  let cancelled = false;
  void (async () => {
    try {
      setLoadingProgress('Refreshing schedule after baseline...');
      store.setLoading(true);
      await reloadScheduleFromServer({ reason: 'baseline-rewrite' });
      if (cancelled) return;
      notifications.show({
        title: 'Baseline finished',
        message: 'Schedule refreshed with the latest baseline data.',
        color: 'teal',
      });
      setBaselineModalOpen(false);
      void refreshActiveRevision();
    } catch (err) {
      console.error('[Set baseline] Post-completion refresh failed:', err);
    } finally {
      if (!cancelled) {
        setLoadingProgress('');
        store.setLoading(false);
      }
    }
  })();
  return () => {
    cancelled = true;
  };
}, [baselineInProgress, projectId, store, reloadScheduleFromServer, refreshActiveRevision]);

  return {
    baselineModalOpen,
    setBaselineModalOpen,
    baselineModalStep,
    setBaselineModalStep,
    baselineSubmitting,
    setBaselineSubmitting,
    baselineModalBlockedMessage,
    setBaselineModalBlockedMessage,
    revisionActionBusy,
    setRevisionActionBusy,
    compareBaselinesEnabled,
    setCompareBaselinesEnabled,
    compareBaselineIndex,
    setCompareBaselineIndex,
    softDeletedBaselineIndices,
    setSoftDeletedBaselineIndices,
    baselineWasInProgressRef,
    baselineStartPendingRef,
    rejectedSnapshotRefreshAttemptedRef,
    baselineCompareOptions,
    baselineVarianceSummary,
    compareBaselineLabel,
    closeBaselineModal,
    handleOpenSetBaseline,
    handleBaselineSaveAndContinue,
    handleConfirmCreateBaseline,
    runRevisionAction,
    handleSubmitActiveRevision,
    handleRefreshActiveRevision,
    handleDiscardActiveRevision,
    handleApproveActiveRevision,
    handleRejectActiveRevision,
  };
}

